import { Pill } from '@eventbrite/eds-pill';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { Icon } from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { FunctionComponent, ReactElement } from 'react';
import { TagIcon } from './TagIcon';
import { UnlockedLockIcon } from './UnlockedLockIcon';

import RebrandingDiscountIcon from '../../../resources/rebranding-signal-discount';
import RebrandingUnlockedIcon from '../../../resources/rebranding-signal-unlocked';

import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import defaultStyles from './PromoCodePills.module.scss';
import rebrandStyles from './PromoCodePills.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export interface PromoCodePillsProps {
    isBOGO: boolean;
    isDiscounted: boolean;
    isUnlocked: boolean;
    className: string;
    isAppRebrandActive: boolean;
}

const PromoPill = ({
    bgColor,
    icon,
    testId,
    text,
    styles,
}: {
    bgColor: 'purple' | 'yellow';
    icon: ReactElement;
    testId?: string;
    text: GenericLazyString | string;
    styles: Record<string, string>;
}) => (
    <Pill
        className={styles.pill}
        dataTestid={testId}
        color={bgColor}
        textColor="dark-grey"
    >
        <Icon size="xsmall">{icon}</Icon>
        <span className={styles.text}>{text}</span>
    </Pill>
);

const PromoCodePills: FunctionComponent<PromoCodePillsProps> = ({
    isBOGO,
    isDiscounted,
    isUnlocked,
    className,
    isAppRebrandActive,
}) => {
    const pillsArray = [];

    const theme = getStyles(!!isAppRebrandActive);
    const styles = stylesModules[theme];

    if (isBOGO) {
        const icon = isAppRebrandActive ? (
            <RebrandingDiscountIcon />
        ) : (
            <TagIcon />
        );

        pillsArray.push(
            <PromoPill
                key="bogo-pill"
                bgColor="yellow"
                icon={icon}
                testId="bogo-pill"
                text={gettext('2 for 1 deal')}
                styles={styles}
            />,
        );
    }

    if (isDiscounted) {
        const icon = isAppRebrandActive ? (
            <RebrandingDiscountIcon />
        ) : (
            <TagIcon />
        );
        pillsArray.push(
            <PromoPill
                key="discounted-pill"
                bgColor="yellow"
                icon={icon}
                testId="discounted-pill"
                text={gettext('Discount applied')}
                styles={styles}
            />,
        );
    }

    if (isUnlocked) {
        const icon = isAppRebrandActive ? (
            <RebrandingUnlockedIcon />
        ) : (
            <UnlockedLockIcon />
        );
        pillsArray.push(
            <PromoPill
                key="unlocked-pill"
                bgColor="purple"
                icon={icon}
                testId="unlocked-pill"
                text={gettext('Unlocked')}
                styles={styles}
            />,
        );
    }

    const wrapperClass = classNames(styles.wrapper, className);
    return pillsArray.length > 0 ? (
        <ul className={wrapperClass} data-testid="promo-code-pills">
            {pillsArray.map((pill, index) => (
                <li key={index}>{pill}</li>
            ))}
        </ul>
    ) : null;
};

export default PromoCodePills;
